import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle } from "lucide-react";
import { SUBJECT_COMBINATIONS, DISCLAIMERS } from "@/data/content";
import { SectionHeading } from "@/components/site/primitives";
import { useApply } from "@/context/ApplyContext";

export function SubjectCombinations({ only }) {
  const tabs = only ? [only] : ["JUPEB", "IJMB", "REMEDIAL"];
  const [tab, setTab] = useState(tabs[0]);
  const [open, setOpen] = useState(null);
  const apply = useApply();
  const combos = SUBJECT_COMBINATIONS[tab] || [];

  return (
    <section className="py-20 sm:py-28 bg-white" data-testid="subject-combinations-section">
      <div className="container-x">
        <SectionHeading overline="Subject Combinations" title="Explore Subject Combinations"
          subtitle="Select your intended course area to view relevant subject combinations. All combinations are editable and may vary by session." />

        {!only && (
          <div className="mt-8 inline-flex rounded-sm border border-slate-200 bg-[#F7F5EF] p-1">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => { setTab(t); setOpen(null); }}
                data-testid={`subject-tab-${t.toLowerCase()}`}
                className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wide rounded-sm transition-all ${
                  tab === t ? "bg-[#0A1E4E] text-[#D4AF37]" : "text-slate-600 hover:text-[#0A1E4E]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
          {combos.map((c, i) => (
            <div key={c.area}>
              <button
                className="flex w-full items-center justify-between py-5 text-left"
                onClick={() => setOpen(open === i ? null : i)}
                data-testid={`subject-accordion-${i}`}
              >
                <span className="font-serif text-lg font-bold text-[#0A1E4E]">{c.area}</span>
                <ChevronDown size={20} className={`text-[#D4AF37] transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }} className="overflow-hidden"
                  >
                    <div className="pb-5">
                      <div className="rounded-sm bg-[#F7F5EF] p-4 text-slate-700">
                        <span className="text-xs font-bold uppercase tracking-wide text-[#C0202A]">Suggested Subjects</span>
                        <p className="mt-1 font-medium">{c.subjects}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <p className="mt-6 text-sm text-slate-500">{DISCLAIMERS.courseChoice}</p>

        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 rounded-sm border border-slate-200 bg-[#F7F5EF] p-6">
          <HelpCircle size={28} className="text-[#0A1E4E]" />
          <div className="flex-1">
            <h4 className="font-serif text-lg font-bold text-[#0A1E4E]">Not sure which combination is right for you?</h4>
            <p className="text-sm text-slate-600">Our admissions officers will guide you based on your intended course.</p>
          </div>
          <button className="btn-red whitespace-nowrap" onClick={() => apply?.openApply()} data-testid="subject-talk-officer">
            Talk to an Admissions Officer
          </button>
        </div>
      </div>
    </section>
  );
}
